/**
 * FolderGlyph — le dossier de l'app, d'après celui du Finder.
 *
 * POURQUOI un glyphe maison plutôt que Lucide : Lucide est un jeu de TRAITS, et
 * son dossier en contour fin faisait daté à côté du reste de l'interface.
 *
 * La FORME vient du Finder : deux plans — pochette derrière, rabat devant plus
 * clair, c'est le contraste entre les deux qui fait lire le pli —, un dégradé
 * court sur le rabat, des proportions larges et basses, une languette ample
 * (45 % de la largeur) et des rayons généreux.
 *
 * La COULEUR, elle, ne vient surtout pas d'Apple. Un bleu franc de Finder est
 * la seule note vive d'une interface par ailleurs entièrement neutre, et sur
 * une grille de dix dossiers il fait une rangée de taches. Le parti retenu est
 * celui des outils juridiques haut de gamme : une encre bleue si désaturée
 * qu'elle passe pour un gris, et ne se révèle bleue qu'à côté d'un vrai gris.
 *
 * Ce n'est pas non plus `--accent-blue` posé à faible opacité — la tentative
 * précédente : ce token est un navy très sombre (220 100% 25%), et son lavis
 * sur blanc ne donne pas un bleu clair mais un gris lavande. Les trois valeurs
 * ci-dessous sont donc écrites en clair, choisies pour l'objet.
 *
 * Pas de contour, pas d'ombre portée : le relief appartient à la CARTE
 * (`cardRelief.ts`), le symbole ne fait que se reconnaître.
 *
 * L'identifiant du dégradé dérive de `useId` : la grille rend N dossiers, et N
 * `<linearGradient id="...">` identiques dans le document seraient des doublons
 * d'id.
 */

import { useId } from "react";
import { cn } from "@/lib/utils";

/** Pochette arrière, languette comprise. */
const BACK =
  "M2 7.2a2.2 2.2 0 0 1 2.2-2.2h4.9a2.2 2.2 0 0 1 1.62.71l1.06 1.15a2.2 2.2 0 0 0 1.62.71h6.4A2.2 2.2 0 0 1 22 9.8v7a2.2 2.2 0 0 1-2.2 2.2H4.2A2.2 2.2 0 0 1 2 16.8V7.2Z";

/** Rabat avant, posé par-dessus. */
const FRONT =
  "M2 10.4a2.2 2.2 0 0 1 2.2-2.2h15.6a2.2 2.2 0 0 1 2.2 2.2v6.4a2.2 2.2 0 0 1-2.2 2.2H4.2A2.2 2.2 0 0 1 2 16.8v-6.4Z";

export interface FolderGlyphProps {
  /** Taille et, au besoin, opacité. Défaut : 32 px. */
  className?: string;
}

export function FolderGlyph({ className }: FolderGlyphProps) {
  // `useId` rend des identifiants de la forme « :r1: ». Les deux-points sont
  // valides en HTML mais piègent tout code qui voudrait viser l'élément par un
  // sélecteur CSS — on les retire, l'unicité ne tient qu'au compteur.
  const gradientId = `folder-front-${useId().replace(/:/g, "")}`;

  return (
    <svg
      viewBox="0 0 24 24"
      className={cn("h-8 w-8 flex-shrink-0", className)}
      aria-hidden="true"
      focusable="false"
    >
      <defs>
        <linearGradient id={gradientId} x1="0" y1="8.2" x2="0" y2="19" gradientUnits="userSpaceOnUse">
          <stop offset="0" stopColor="hsl(213 22% 58%)" />
          <stop offset="1" stopColor="hsl(214 23% 45%)" />
        </linearGradient>
      </defs>

      <path d={BACK} fill="hsl(215 24% 34%)" />
      <path d={FRONT} fill={`url(#${gradientId})`} />
    </svg>
  );
}
